import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import CommunityLayout from '../../../components/community/CommunityLayout';
import NoticeCard from '../../../components/common/NoticeCard';
import LoginModal from '../../../components/modals/LoginModal';
import { getPostList, PostListItem, ApiResponse, PostListResponse } from '../../../services/postApi';
import { searchLectureQuestionPosts } from '../../../services/searchApi';
import { useInfiniteScroll } from '../../../hooks/useInfiniteScroll';
import type { PostSearchItem } from '../../../services/searchApi';
import { 
  getLectureCategories, 
  getLecturesByCategory, 
  getLectureChapters,
  type LectureCategory,
  type LectureItem,
  type LectureChapter
} from '../../../services/lecturerApi';

// HTML 태그 제거 함수 (코드, 이미지, 첨부파일은 완전히 제거)
const stripHtmlExceptCodeAndMedia = (html: string): string => {
  // 임시 div 엘리먼트 생성
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  
  // 코드 블록 완전히 제거
  const preElements = tempDiv.querySelectorAll('pre');
  preElements.forEach((pre) => {
    pre.remove();
  });
  
  // 인라인 코드 완전히 제거
  const codeElements = tempDiv.querySelectorAll('code');
  codeElements.forEach((code) => {
    code.remove();
  });
  
  // 이미지 태그 완전히 제거
  const imgElements = tempDiv.querySelectorAll('img');
  imgElements.forEach((img) => {
    img.remove();
  });
  
  // 첨부파일 관련 모든 요소 완전히 제거
  const attachmentLists = tempDiv.querySelectorAll('ul.tbc-attachments, ul');
  attachmentLists.forEach((ul) => {
    // 첨부파일 목록인지 확인 (li 안에 a 태그가 있는 경우)
    const hasAttachmentLinks = ul.querySelectorAll('li a').length > 0;
    if (hasAttachmentLinks) {
      ul.remove();
    }
  });
  
  // 모든 종류의 첨부파일 링크 완전히 제거
  const attachmentLinks = tempDiv.querySelectorAll('a[download], a[href*="blob:"], a[href*=".pdf"], a[href*=".doc"], a[href*=".zip"], a[href*=".jpg"], a[href*=".png"], a[href*=".gif"]');
  attachmentLinks.forEach((link) => {
    link.remove();
  });
  
  // 첨부파일 관련 클래스가 있는 모든 요소 제거
  const attachmentElements = tempDiv.querySelectorAll('.tbc-attachment, .tbc-attachment__link, .tbc-attachment__name, .attachment');
  attachmentElements.forEach((element) => {
    element.remove();
  });
  
  // 파일 확장자가 포함된 링크나 텍스트 제거
  const fileExtensionPattern = /\.(pdf|doc|docx|xls|xlsx|ppt|pptx|zip|rar|7z|tar|gz|jpg|jpeg|png|gif|bmp|svg|mp4|avi|mov|mp3|wav|txt|csv)$/i;
  const allLinks = tempDiv.querySelectorAll('a');
  allLinks.forEach((link) => {
    const href = link.getAttribute('href') || '';
    const text = link.textContent || '';
    if (fileExtensionPattern.test(href) || fileExtensionPattern.test(text)) {
      link.remove();
    }
  });
  
  // 나머지 HTML 태그만 제거하고 텍스트 내용 추출
  let textContent = tempDiv.textContent || tempDiv.innerText || '';
  
  // 연속된 공백과 줄바꿈 정리
  return textContent.replace(/\s+/g, ' ').trim();
};

const LectureQuestions: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [searchValue, setSearchValue] = useState('');
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [postList, setPostList] = useState<PostListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMorePosts, setHasMorePosts] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<number | undefined>();
  const [selectedLecture, setSelectedLecture] = useState<number | undefined>();
  const [selectedChapter, setSelectedChapter] = useState<number | undefined>();
  
  // Redux 상태에서 로그인 정보 가져오기
  const { token } = useSelector((state: RootState) => state.auth);
  
  // 로그인 모달 상태
  const [showLoginModal, setShowLoginModal] = useState(false);
  
  // 로그인 후 이동할 게시글 ID 저장
  const [pendingPostId, setPendingPostId] = useState<number | null>(null);
  
  // 검색 관련 상태
  const [searchResults, setSearchResults] = useState<PostSearchItem[]>([]);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  // API 데이터 상태
  const [categories, setCategories] = useState<LectureCategory[]>([]);
  const [lectures, setLectures] = useState<LectureItem[]>([]);
  const [chapters, setChapters] = useState<LectureChapter[]>([]);
  const [selectedCategoryData, setSelectedCategoryData] = useState<LectureCategory | null>(null);
  const [selectedLectureData, setSelectedLectureData] = useState<LectureItem | null>(null);
  
  // API 로딩 상태
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [lecturesLoading, setLecturesLoading] = useState(false);
  const [chaptersLoading, setChaptersLoading] = useState(false);

  // 게시글 목록 조회
  const fetchPosts = useCallback(async (pageNum: number = 0, reset: boolean = false) => {
    /// console.log('강의 질문 목록 API 호출:', { pageNum, reset, loading });
    if (loading) return;
    
    setLoading(true);
    try {
      // /// console.log('API 요청 시작 - board: lecture');
      const response: ApiResponse<PostListResponse> = await getPostList(
        'lecture',
        pageNum,
        10,
        selectedCategory,
        selectedLecture,
        selectedChapter
      );

      /// console.log('강의 질문 목록 API 응답:', {response});
      if (response.data) {
        const newPosts = response.data.content;
        // /// console.log('받은 게시글 수:', newPosts.length);
        setPostList(reset ? newPosts : prev => [...prev, ...newPosts]);
        setHasMorePosts(!response.data.last);
      }
    } catch (error: any) {
      /// console.error('게시글 목록 조회 실패:', error);
      if (error.message === 'Unauthorized') {
        navigate('/login');
        return;
      }
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, selectedLecture, selectedChapter, navigate]);

  // 컴포넌트 마운트 시 데이터 로드
  useEffect(() => {
    // /// console.log('useEffect 실행됨 - 데이터 로드 시작');
    if (!isSearchMode) {
      fetchPosts(0, true);
    }
  }, [fetchPosts, isSearchMode]);

  // URL에서 검색 키워드 확인 및 검색 실행
  useEffect(() => {
    const keyword = searchParams.get('keyword');
    if (keyword && keyword.trim()) {
      setSearchValue(keyword);
      setIsSearchMode(true);
      performSearch(keyword);
    } else {
      setIsSearchMode(false);
      setSearchResults([]);
    }
  }, [searchParams]);

  // 검색 실행 함수
  const performSearch = async (keyword: string) => {
    if (!keyword.trim()) return;

    setSearchLoading(true);
    setSearchError(null);

    try {
      const results = await searchLectureQuestionPosts(keyword);
      setSearchResults(results);
    } catch (error) {
      /// console.error('강의 질문 검색 오류:', error);
      setSearchError('검색 중 오류가 발생했습니다.');
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  // 컴포넌트 마운트 시 카테고리 로드
  useEffect(() => {
    let isCancelled = false;
    
    const loadCategories = async () => {
      setCategoriesLoading(true);
      try {
        const response = await getLectureCategories();
        if (!isCancelled && response.code === 200) {
          setCategories(response.data);
        }
      } catch (error) {
        if (!isCancelled) {
          /// console.error('카테고리 로드 실패:', error);
        }
      } finally {
        if (!isCancelled) {
          setCategoriesLoading(false);
        }
      }
    };

    // 카테고리가 이미 로드되지 않았을 때만 로드
    if (categories.length === 0) {
      loadCategories();
    }
    
    return () => {
      isCancelled = true;
    };
  }, [categories.length]);

  // 강의 목록 로드
  const loadLectures = useCallback(async (categoryRouteName: string) => {
    setLecturesLoading(true);
    try {
      const response = await getLecturesByCategory(categoryRouteName);
      if (response.code === 200) {
        setLectures(response.data.content);
      }
    } catch (error) {
      /// console.error('강의 목록 로드 실패:', error);
    } finally {
      setLecturesLoading(false);
    }
  }, []);

  // 챕터 목록 로드
  const loadChapters = useCallback(async (lectureId: number) => {
    setChaptersLoading(true);
    try {
      const response = await getLectureChapters(lectureId);
      if (response.code === 200) {
        setChapters(response.data);
      }
    } catch (error) {
      /// console.error('챕터 목록 로드 실패:', error);
    } finally {
      setChaptersLoading(false);
    }
  }, []);

  // 카테고리 선택 시 강의 목록 로드
  useEffect(() => {
    if (selectedCategoryData?.route_name) {
      setLectures([]);
      setChapters([]);
      setSelectedLectureData(null);
      setSelectedLecture(undefined);
      setSelectedChapter(undefined);
      loadLectures(selectedCategoryData.route_name);
    }
  }, [selectedCategoryData, loadLectures]);

  // 강의 선택 시 챕터 목록 로드
  useEffect(() => {
    if (selectedLectureData?.lecture_info.id) {
      setChapters([]);
      setSelectedChapter(undefined);
      loadChapters(selectedLectureData.lecture_info.id);
    }
  }, [selectedLectureData, loadChapters]);

  // 더 많은 데이터 로드
  const loadMorePosts = useCallback(() => {
    if (hasMorePosts && !loading && !isSearchMode) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchPosts(nextPage, false);
    }
  }, [hasMorePosts, loading, isSearchMode, page, fetchPosts]);

  // 무한스크롤 적용
  useInfiniteScroll({
    hasMore: hasMorePosts && !isSearchMode,
    isLoading: loading,
    onLoadMore: loadMorePosts,
    threshold: 200
  });

  // 게시글 클릭 핸들러
  const handlePostClick = (postId: number) => {
    // /// console.log('=== 게시글 클릭 ===');
    // /// console.log('isAuthenticated:', isAuthenticated);
    // /// console.log('redux token:', token);
    // /// console.log('localStorage access_token:', localStorage.getItem('access_token'));
    
    // Redux와 localStorage 둘 다 확인
    const reduxToken = token;
    const localToken = localStorage.getItem('access_token');
    const finalToken = reduxToken || localToken;
    
    // 토큰이 없으면 로그인 모달 띄우기
    if (!finalToken) {
      setPendingPostId(postId); // 로그인 후 이동할 게시글 ID 저장
      setShowLoginModal(true);
      return;
    }
    
    // 토큰이 있으면 게시글 페이지로 이동
    // /// console.log('로그인되어 있습니다. 게시글 페이지로 이동합니다.');
    navigate(`/community/post/${postId}`);
  };

  // 로그인 성공 후 핸들러
  const handleLoginSuccess = () => {
    setShowLoginModal(false);
    
    // 로그인 후 대기 중인 게시글로 자동 이동
    if (pendingPostId) {
      // /// console.log(`로그인 완료. 게시글 ${pendingPostId}로 이동합니다.`);
      navigate(`/community/post/${pendingPostId}`);
      setPendingPostId(null); // 상태 초기화
    }
  };

  // 로그인 모달 닫기 핸들러
  const handleLoginModalClose = () => {
    setShowLoginModal(false);
    setPendingPostId(null); // 모달을 닫을 때도 상태 초기화
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  const handleSearchSubmit = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const searchTerm = (e.target as HTMLInputElement).value.trim();
      if (searchTerm) {
        // URL 파라미터 변경하여 검색 실행
        navigate(`/community/lecture-questions?keyword=${encodeURIComponent(searchTerm)}`);
      } else {
        // 검색어가 비어있으면 기본 페이지로 이동
        navigate('/community/lecture-questions');
      }
    }
  };

  const handleDropdownToggle = (dropdownName: string) => {
    setOpenDropdown(openDropdown === dropdownName ? null : dropdownName);
  };

  // 카테고리 선택 핸들러
  const handleCategorySelect = (category: LectureCategory) => {
    setSelectedCategoryData(category);
    setSelectedCategory(category.id);
    setOpenDropdown(null);
  };

  // 강의 선택 핸들러
  const handleLectureSelect = (lecture: LectureItem) => {
    setSelectedLectureData(lecture);
    setSelectedLecture(lecture.lecture_info.id);
    setOpenDropdown(null);
  };

  // 챕터 선택 핸들러 (인덱스 기반으로 1부터 시작)
  const handleChapterSelect = (chapter: LectureChapter | null, index: number | null) => {
    if (chapter === null) {
      // "전체" 선택 시
      setSelectedChapter(undefined);
    } else if (index !== null) {
      setSelectedChapter(index + 1); // 1부터 시작하는 번호
    }
    setOpenDropdown(null);
  };

  // 공지사항 필터링 (pinned=true인 게시글)
  const pinnedPosts = postList.filter(post => post.pinned);
  const regularPosts = postList.filter(post => !post.pinned);

  return (
    <CommunityLayout>
      <Container>
        <MainContainer>
          {/* 페이지 헤더 */}
          <PageHeader>
            <PageTitle>강의 질문</PageTitle>
          </PageHeader>

          {/* 브레드크럼과 검색바 */}
          <NavigationBar>
            <BreadcrumbContainer>
              <BreadcrumbItem>홈</BreadcrumbItem>
              <ChevronIcon>
                <svg width="8" height="16" viewBox="0 0 8 16" fill="none">
                  <path 
                    d="M1.73 3.07L6.26 7.6L1.73 12.13" 
                    stroke="rgba(55, 56, 60, 0.61)" 
                    strokeWidth="1.5" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
              </ChevronIcon>
              <BreadcrumbItem>커뮤니티</BreadcrumbItem>
              <ChevronIcon>
                <svg width="8" height="16" viewBox="0 0 8 16" fill="none">
                  <path 
                    d="M1.73 3.07L6.26 7.6L1.73 12.13" 
                    stroke="rgba(55, 56, 60, 0.61)" 
                    strokeWidth="1.5" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
              </ChevronIcon>
              <BreadcrumbItem>강의 질문</BreadcrumbItem>
            </BreadcrumbContainer>
            
            <SearchContainer>
              <SearchBox>
                <SearchIcon>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path 
                      d="M14.87 14.87L10.4 10.4M12 6.5C12 9.54 9.54 12 6.5 12C3.46 12 1 9.54 1 6.5C1 3.46 3.46 1 6.5 1C9.54 1 12 3.46 12 6.5Z" 
                      stroke="rgba(55, 56, 60, 0.28)" 
                      strokeWidth="1.5" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                  </svg>
                </SearchIcon>
                <SearchInput 
                  type="text" 
                  placeholder="검색어를 입력해주세요."
                  value={searchValue}
                  onChange={handleSearchChange}
                  onKeyUp={handleSearchSubmit}
                />
              </SearchBox>
            </SearchContainer>
          </NavigationBar>

          {/* 카테고리 필터 */}
          <CategoryFilterSection>
            <CategoryContainer>
              <DropdownContainer>
                <CategoryButton 
                  active={openDropdown === 'category'}
                  onClick={() => handleDropdownToggle('category')}
                >
                  <CategoryText>
                    {selectedCategoryData ? selectedCategoryData.name : '카테고리'}
                  </CategoryText>
                  <DropdownIcon>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M8.0865 8.57936C7.71442 9.0373 7.52838 9.26627 7.3044 9.3494C7.10797 9.42231 6.89191 9.42231 6.69548 9.3494C6.4715 9.26627 6.28546 9.0373 5.91338 8.57936L5.06308 7.53283C4.4701 6.80301 4.17361 6.4381 4.17165 6.13058C4.16994 5.86319 4.2906 5.60969 4.49921 5.44239C4.73911 5.25 5.20928 5.25 6.14963 5.25H7.85025C8.7906 5.25 9.26077 5.25 9.50067 5.44239C9.70928 5.60969 9.82994 5.86319 9.82823 6.13058C9.82627 6.4381 9.52978 6.80301 8.9368 7.53283L8.0865 8.57936Z" fill="black"/>
                    </svg>
                  </DropdownIcon>
                </CategoryButton>
                {openDropdown === 'category' && (
                  <DropdownMenu>
                    {categoriesLoading ? (
                      <DropdownItem>로딩 중...</DropdownItem>
                    ) : (
                      categories.map((category) => (
                        <DropdownItem 
                          key={category.id}
                          onClick={() => handleCategorySelect(category)}
                        >
                          {category.name}
                        </DropdownItem>
                      ))
                    )}
                  </DropdownMenu>
                )}
              </DropdownContainer>

              <DropdownContainer>
                <CategoryButton 
                  active={openDropdown === 'lecture'}
                  onClick={() => handleDropdownToggle('lecture')}
                  disabled={!selectedCategoryData}
                >
                  <CategoryText>
                    {selectedLectureData ? selectedLectureData.lecture_info.title : '강의'}
                  </CategoryText>
                  <DropdownIcon>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M8.0865 8.57936C7.71442 9.0373 7.52838 9.26627 7.3044 9.3494C7.10797 9.42231 6.89191 9.42231 6.69548 9.3494C6.4715 9.26627 6.28546 9.0373 5.91338 8.57936L5.06308 7.53283C4.4701 6.80301 4.17361 6.4381 4.17165 6.13058C4.16994 5.86319 4.2906 5.60969 4.49921 5.44239C4.73911 5.25 5.20928 5.25 6.14963 5.25H7.85025C8.7906 5.25 9.26077 5.25 9.50067 5.44239C9.70928 5.60969 9.82994 5.86319 9.82823 6.13058C9.82627 6.4381 9.52978 6.80301 8.9368 7.53283L8.0865 8.57936Z" fill="black"/>
                    </svg>
                  </DropdownIcon>
                </CategoryButton>
                {openDropdown === 'lecture' && selectedCategoryData && (
                  <DropdownMenu>
                    {lecturesLoading ? (
                      <DropdownItem>로딩 중...</DropdownItem>
                    ) : lectures?.length === 0 ? (
                      <DropdownItem>강의가 없습니다</DropdownItem>
                    ) : (
                      lectures?.map((lecture) => (
                        <DropdownItem 
                          key={lecture.lecture_info.id}
                          onClick={() => handleLectureSelect(lecture)}
                        >
                          {lecture.lecture_info.title}
                        </DropdownItem>
                      ))
                    )}
                  </DropdownMenu>
                )}
              </DropdownContainer>

              <DropdownContainer>
                <CategoryButton 
                  active={openDropdown === 'chapter'}
                  onClick={() => handleDropdownToggle('chapter')}
                  disabled={!selectedLectureData}
                >
                  <CategoryText>
                    {selectedChapter === undefined ? '챕터' : `챕터 ${selectedChapter}`}
                  </CategoryText>
                  <DropdownIcon>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M8.0865 8.57936C7.71442 9.0373 7.52838 9.26627 7.3044 9.3494C7.10797 9.42231 6.89191 9.42231 6.69548 9.3494C6.4715 9.26627 6.28546 9.0373 5.91338 8.57936L5.06308 7.53283C4.4701 6.80301 4.17361 6.4381 4.17165 6.13058C4.16994 5.86319 4.2906 5.60969 4.49921 5.44239C4.73911 5.25 5.20928 5.25 6.14963 5.25H7.85025C8.7906 5.25 9.26077 5.25 9.50067 5.44239C9.70928 5.60969 9.82994 5.86319 9.82823 6.13058C9.82627 6.4381 9.52978 6.80301 8.9368 7.53283L8.0865 8.57936Z" fill="black"/>
                    </svg>
                  </DropdownIcon>
                </CategoryButton>
                {openDropdown === 'chapter' && selectedLectureData && (
                  <DropdownMenu>
                    {chaptersLoading ? (
                      <DropdownItem>로딩 중...</DropdownItem>
                    ) : chapters.length === 0 ? (
                      <DropdownItem>챕터가 없습니다</DropdownItem>
                    ) : (
                      <>
                        <DropdownItem 
                          onClick={() => handleChapterSelect(null, null)}
                        >
                          전체
                        </DropdownItem>
                        {chapters.map((chapter, index) => (
                          <DropdownItem 
                            key={chapter.uri}
                            onClick={() => handleChapterSelect(chapter, index)}
                          >
                            챕터 {index + 1}: {chapter.title}
                          </DropdownItem>
                        ))}
                      </>
                    )}
                  </DropdownMenu>
                )}
              </DropdownContainer>
            </CategoryContainer>
          </CategoryFilterSection>

          {/* 상단고정 공지사항 */}
          {pinnedPosts.length > 0 && (
            <NoticeSection>
              {pinnedPosts.map(notice => (
                <NoticeCard 
                  key={notice.post_id}
                  title={notice.title}
                  onClick={() => handlePostClick(notice.post_id)}
                />
              ))}
            </NoticeSection>
          )}

          {/* 게시글 목록 */}
          <QuestionsList>
            {isSearchMode ? (
              // 검색 모드
              <>
                {searchLoading ? (
                  <LoadingState>검색 중...</LoadingState>
                ) : searchError ? (
                  <EmptyState>{searchError}</EmptyState>
                ) : searchResults.length === 0 ? (
                  <EmptyState>'{searchValue}'에 대한 검색 결과가 없습니다.</EmptyState>
                ) : (
                  searchResults.map((question, index) => (
                    <React.Fragment key={question.id}>
                      <QuestionCard onClick={() => handlePostClick(question.id)}>
                        <QuestionContent>
                          <QuestionHeader>
                            <QuestionTitle>{question.title}</QuestionTitle>
                            <QuestionDescription>
                              {stripHtmlExceptCodeAndMedia(question.htmlcontent)}
                            </QuestionDescription>
                          </QuestionHeader>
                          
                          <QuestionMeta>
                            <QuestionCategoryTags>
                              <QuestionCategory>{question.category}</QuestionCategory>
                              {question.lectureTitle && (
                                <>
                                  <Divider />
                                  <QuestionSubCategory>{question.lectureTitle}</QuestionSubCategory>
                                </>
                              )}
                              {question.pinned && (
                                <>
                                  <Divider />
                                  <QuestionChapter style={{backgroundColor: '#ff6b6b', color: 'white'}}>📌 고정</QuestionChapter>
                                </>
                              )}
                            </QuestionCategoryTags>
                          </QuestionMeta>
                        </QuestionContent>
                        
                        <QuestionFooter>
                          <QuestionAuthorInfo>
                            <AuthorAvatar>
                              <img src={question.writerProfileImageUrl || "/images/default-avatar.png"} alt="작성자" />
                            </AuthorAvatar>
                            <AuthorName>{question.writerName}</AuthorName>
                            <Divider />
                            <QuestionDate>{new Date(question.postDate).toLocaleString('ko-KR', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                              hour12: false
                            })}</QuestionDate>
                          </QuestionAuthorInfo>
                          
                          <QuestionStats>
                            <StatItem>답변 -</StatItem>
                            <Divider />
                            <StatItem>조회수 -</StatItem>
                          </QuestionStats>
                        </QuestionFooter>
                      </QuestionCard>
                      
                      {index < searchResults.length - 1 && <QuestionDivider />}
                    </React.Fragment>
                  ))
                )}
              </>
            ) : (
              // 일반 모드 (기본 데이터)
              <>
                {loading && postList.length === 0 ? (
                  <LoadingState>게시글을 불러오는 중...</LoadingState>
                ) : postList.length === 0 ? (
                  <EmptyState>작성된 게시글이 없습니다.</EmptyState>
                ) : (
                  regularPosts.map((question, index) => (
                    <React.Fragment key={question.post_id}>
                      <QuestionCard onClick={() => handlePostClick(question.post_id)}>
                        <QuestionContent>
                          <QuestionHeader>
                            <QuestionTitle>{question.title}</QuestionTitle>
                            <QuestionDescription>
                              {stripHtmlExceptCodeAndMedia(question.content)}
                            </QuestionDescription>
                          </QuestionHeader>
                          
                          <QuestionMeta>
                            <QuestionCategoryTags>
                              <QuestionCategory>{question.post_category}</QuestionCategory>
                              {question.lecture_name && (
                                <>
                                  <Divider />
                                  <QuestionSubCategory>{question.lecture_name}</QuestionSubCategory>
                                </>
                              )}
                              {Number(question.chapter) !== 0 && (
                                <>
                                  <Divider />
                                  <QuestionChapter>챕터 {question.chapter}</QuestionChapter>
                                </>
                              )}
                            </QuestionCategoryTags>
                          </QuestionMeta>
                        </QuestionContent>
                        
                        <QuestionFooter>
                          <QuestionAuthorInfo>
                            <AuthorAvatar>
                              <img src={question.writer_profile_image_url || "/images/default-avatar.png"} alt="작성자" />
                            </AuthorAvatar>
                            <AuthorName>{question.writer_name}</AuthorName>
                            <Divider />
                            <QuestionDate>{new Date(question.created_at).toLocaleString('ko-KR', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                              hour12: false
                            })}</QuestionDate>
                          </QuestionAuthorInfo>
                          
                          <QuestionStats>
                            <StatItem>답변 {question.reply_cnt}</StatItem>
                            <Divider />
                            <StatItem>조회수 {question.view_cnt}</StatItem>
                          </QuestionStats>
                        </QuestionFooter>
                      </QuestionCard>
                      
                      {index < regularPosts.length - 1 && <QuestionDivider />}
                    </React.Fragment>
                  ))
                )}
                
                {/* 무한스크롤 로딩 표시 */}
                {loading && regularPosts.length > 0 && (
                  <LoadingState>추가 게시글을 불러오는 중...</LoadingState>
                )}
              </>
            )}
          </QuestionsList>
        </MainContainer>
        
        {/* 로그인 모달 */}
        <LoginModal
          isOpen={showLoginModal}
          onClose={handleLoginModalClose}
          onLoginSuccess={handleLoginSuccess}
        />
      </Container>
    </CommunityLayout>
  );
};

// 스타일 컴포넌트들
const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
`;

const MainContainer = styled.div`
  width: 100%;
  max-width: 894px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 32px;
`;

const PageHeader = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 16px;
  margin-top: 60px;
`;

const PageTitle = styled.h1`
  color: #171719;
  font-size: 40px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 700;
  line-height: 52px;
  margin: 0;
`;

const NavigationBar = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
    align-items: flex-start;
  }
`;

const BreadcrumbContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
`;

const BreadcrumbItem = styled.span`
  color: rgba(55, 56, 60, 0.61);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0.09px;
`;

const ChevronIcon = styled.div`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SearchContainer = styled.div`
  width: 282px;
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const SearchBox = styled.div`
  width: 100%;
  padding: 8px;
  background: white;
  box-shadow: 0px 0px 1px rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  outline: 1px solid rgba(112, 115, 124, 0.22);
  outline-offset: -1px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
`;

const SearchIcon = styled.div`
  width: 16px;
  height: 16px;
  padding: 2px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  color: #171719;
  font-size: 12px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 16.01px;
  letter-spacing: 0.30px;
  
  &::placeholder {
    color: rgba(55, 56, 60, 0.28);
  }
`;

const NoticeSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: -32px;
`;

const CategoryFilterSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const CategoryContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
  position: relative;
`;

const DropdownContainer = styled.div`
  position: relative;
`;

const CategoryButton = styled.button<{ active: boolean; disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  padding: 7px 11px;
  background: white;
  border: 1px solid rgba(112, 115, 124, 0.16);
  border-radius: 10px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  opacity: ${props => props.disabled ? 0.6 : 1};
  position: relative;
  height: 36px;
  
  &:hover {
    background: ${props => props.disabled ? 'white' : 'rgba(23, 23, 25, 0.04)'};
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  min-width: 200px;
  width: max-content;
  background: white;
  border: 1px solid rgba(112, 115, 124, 0.22);
  border-radius: 8px;
  box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.08);
  z-index: 1000;
  margin-top: 4px;
  overflow: hidden;
`;

const DropdownItem = styled.div`
  padding: 8px 16px;
  height: 36px;
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  color: rgba(55, 56, 60, 0.88);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 20px;
  
  &:hover {
    background: rgba(112, 115, 124, 0.08);
  }
  
  &:not(:last-child) {
    border-bottom: 1px solid rgba(112, 115, 124, 0.08);
  }
`;

const CategoryText = styled.span`
  color: var(--Label-Strong, black);
  font-size: 15px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 22.01px;
  letter-spacing: 0.14px;
  padding: 0 2px;
`;

const DropdownIcon = styled.div`
  width: 14px;
  height: 14px;
  color: var(--Label-Strong, black);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px 0;
  
  svg {
    width: 14px;
    height: 14px;
  }
`;

const QuestionsList = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const QuestionCard = styled.div`
  width: 100%;
  padding: 32px 0;
  display: flex;
  flex-direction: column;
  gap: 24px;
  cursor: pointer;
`;

const QuestionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const QuestionHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const QuestionTitle = styled.h3`
  color: #171719;
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.09px;
  margin: 0;
`;

const QuestionDescription = styled.p`
  color: rgba(46, 47, 51, 0.88);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 21.99px;
  letter-spacing: 0.20px;
  margin: 0;
  
  /* 3줄 말줄임 */
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const QuestionMeta = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
`;

const QuestionCategoryTags = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
`;

const QuestionCategory = styled.span`
  color: rgba(55, 56, 60, 0.61);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 20.01px;
  letter-spacing: 0.20px;
`;

const QuestionSubCategory = styled.span`
  color: rgba(55, 56, 60, 0.61);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 20.01px;
  letter-spacing: 0.20px;
`;

const QuestionChapter = styled.span`
  color: rgba(55, 56, 60, 0.61);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 20.01px;
  letter-spacing: 0.20px;
`;

const QuestionFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const QuestionAuthorInfo = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
`;

const AuthorAvatar = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  overflow: hidden;
  outline: 1px solid rgba(112, 115, 124, 0.08);
  outline-offset: -1px;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const AuthorName = styled.span`
  color: rgba(46, 47, 51, 0.88);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 20.01px;
  letter-spacing: 0.20px;
`;

const QuestionDate = styled.span`
  color: rgba(55, 56, 60, 0.61);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 20.01px;
  letter-spacing: 0.20px;
`;

const QuestionStats = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
`;

const StatItem = styled.span`
  color: rgba(55, 56, 60, 0.61);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 20.01px;
  letter-spacing: 0.20px;
`;

const Divider = styled.div`
  width: 1px;
  height: 12px;
  background: rgba(112, 115, 124, 0.22);
`;

const QuestionDivider = styled.div`
  width: 100%;
  height: 1px;
  background: rgba(112, 115, 124, 0.22);
`;

const LoadMoreContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  margin-top: 32px;
`;

const LoadMoreButton = styled.button<{ disabled: boolean }>`
  padding: 12px 24px;
  background: ${props => props.disabled ? '#f5f5f5' : '#448181'};
  color: ${props => props.disabled ? '#999' : 'white'};
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: background-color 0.2s ease;

  &:hover {
    background: ${props => props.disabled ? '#f5f5f5' : '#3a6b6b'};
  }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 40px 0;
  color: rgba(55, 56, 60, 0.61);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 0;
  color: rgba(55, 56, 60, 0.61);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
`;

export default LectureQuestions;